import type { AnalysisCalendarPeriod } from "./analysis-date-range";
import type { ShiftFormManagerDashboard, ShiftFormManagerDashboardFilters } from "../../../shared/shift-production-form/contracts";
import { analysisFactoryFilters, previousAnalysisPeriod } from "./analysis-factory-summary";

export type AnalysisReportResource = {
  dashboard: ShiftFormManagerDashboard | null;
  phase: "loading" | "ready" | "error";
  error: string | null;
  refreshing: boolean;
  refreshError: string | null;
};
export type AnalysisFactoryReports = { current: AnalysisReportResource; previous: AnalysisReportResource };
export const pendingAnalysisReport = (): AnalysisReportResource => ({ dashboard: null, phase: "loading", error: null, refreshing: false, refreshError: null });

const cacheLifetimeMs = 30_000;
const maximumCachedReports = 24;
const reportCache = new Map<string, { dashboard: ShiftFormManagerDashboard; cachedAt: number }>();
const reportReads = new Map<string, Promise<ShiftFormManagerDashboard>>();
const actorGenerations = new Map<string, number>();

function reportScopeKey(actorScope: string, filters: ShiftFormManagerDashboardFilters) {
  const scope = Object.fromEntries(
    Object.entries(analysisFactoryFilters(filters))
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  );
  return `${actorScope}:${JSON.stringify(scope)}`;
}

function reportResource(actorScope: string, filters: ShiftFormManagerDashboardFilters): AnalysisReportResource {
  const dashboard = cachedDashboard(actorScope, filters);
  return dashboard
    ? { dashboard, phase: "ready", error: null, refreshing: true, refreshError: null }
    : pendingAnalysisReport();
}

function cachedDashboard(actorScope: string, filters: ShiftFormManagerDashboardFilters) {
  const key = reportScopeKey(actorScope, filters);
  const cached = reportCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.cachedAt > cacheLifetimeMs) {
    reportCache.delete(key);
    return null;
  }
  return cached.dashboard;
}

function cacheDashboard(actorScope: string, filters: ShiftFormManagerDashboardFilters, dashboard: ShiftFormManagerDashboard) {
  const key = reportScopeKey(actorScope, filters);
  reportCache.delete(key);
  reportCache.set(key, { dashboard, cachedAt: Date.now() });
  while (reportCache.size > maximumCachedReports) reportCache.delete(reportCache.keys().next().value as string);
}

function reportError(reason: unknown) {
  return reason instanceof Error ? reason.message : "Rapor alınamadı.";
}

function invalidatesActorScope(reason: unknown) {
  const code = typeof reason === "object" && reason !== null && "code" in reason && typeof reason.code === "string" ? reason.code : "";
  const status = typeof reason === "object" && reason !== null && "status" in reason && typeof reason.status === "number" ? reason.status : 0;
  return (
    status === 401
    || status === 403
    || /^HTTP_(401|403)$/u.test(code)
    || ["AUTHENTICATION_REQUIRED", "OPERATOR_SCOPE_ISOLATION", "PRODUCTION_MANAGER_REQUIRED"].includes(code)
  );
}

function clearActorReportCache(actorScope: string) {
  actorGenerations.set(actorScope, (actorGenerations.get(actorScope) ?? 0) + 1);
  const prefix = `${actorScope}:`;
  for (const key of reportCache.keys()) if (key.startsWith(prefix)) reportCache.delete(key);
  for (const key of reportReads.keys()) if (key.startsWith(prefix)) reportReads.delete(key);
}

export function clearAnalysisFactoryReportCache(actorScope: string) {
  clearActorReportCache(actorScope);
}

export function cachedAnalysisFactoryReports(actorScope: string, filters: ShiftFormManagerDashboardFilters, period?: AnalysisCalendarPeriod): AnalysisFactoryReports | null {
  const scope = analysisFactoryFilters(filters);
  const prior = previousAnalysisPeriod(scope, period);
  const current = reportResource(actorScope, scope);
  const previous = prior ? reportResource(actorScope, prior) : { dashboard: null, phase: "ready" as const, error: null, refreshing: false, refreshError: null };
  return current.dashboard || previous.dashboard ? { current, previous } : null;
}

function readReport(
  actorScope: string,
  filters: ShiftFormManagerDashboardFilters,
  read: (filters: ShiftFormManagerDashboardFilters) => Promise<ShiftFormManagerDashboard>,
) {
  const key = reportScopeKey(actorScope, filters);
  const active = reportReads.get(key);
  if (active) return active;
  const generation = actorGenerations.get(actorScope) ?? 0;
  const request = read(filters).then(
    dashboard => {
      if ((actorGenerations.get(actorScope) ?? 0) === generation) cacheDashboard(actorScope, filters, dashboard);
      return dashboard;
    },
    reason => {
      if (invalidatesActorScope(reason)) clearActorReportCache(actorScope);
      throw reason;
    },
  ).finally(() => {
    if (reportReads.get(key) === request) reportReads.delete(key);
  });
  reportReads.set(key, request);
  return request;
}

/** Independent read lanes: a failed historical comparison never hides today's report. */
export function loadAnalysisFactoryReports(
  actorScope: string,
  filters: ShiftFormManagerDashboardFilters,
  read: (filters: ShiftFormManagerDashboardFilters) => Promise<ShiftFormManagerDashboard>,
  publish: (reports: AnalysisFactoryReports) => void,
  period?: AnalysisCalendarPeriod,
) {
  let active = true;
  let authorizationRejected = false;
  const generation = actorGenerations.get(actorScope) ?? 0;
  const scope = analysisFactoryFilters(filters);
  const prior = previousAnalysisPeriod(scope, period);
  let reports: AnalysisFactoryReports = {
    current: reportResource(actorScope, scope),
    previous: prior ? reportResource(actorScope, prior) : { dashboard: null, phase: "ready", error: null, refreshing: false, refreshError: null },
  };
  publish(reports);
  const load = async (lane: keyof AnalysisFactoryReports, scope: ShiftFormManagerDashboardFilters) => {
    try {
      const dashboard = await readReport(actorScope, scope, read);
      if (!active || authorizationRejected || (actorGenerations.get(actorScope) ?? 0) !== generation) return;
      reports = { ...reports, [lane]: { dashboard, phase: "ready", error: null, refreshing: false, refreshError: null } };
    } catch (reason) {
      if (!active || authorizationRejected) return;
      if (invalidatesActorScope(reason)) {
        authorizationRejected = true;
        const error = reportError(reason);
        reports = {
          current: { dashboard: null, phase: "error", error, refreshing: false, refreshError: null },
          previous: { dashboard: null, phase: "error", error, refreshing: false, refreshError: null },
        };
        publish(reports);
        return;
      }
      if ((actorGenerations.get(actorScope) ?? 0) !== generation) return;
      const cached = cachedDashboard(actorScope, scope);
      reports = { ...reports, [lane]: cached && !invalidatesActorScope(reason)
        ? { dashboard: cached, phase: "ready", error: null, refreshing: false, refreshError: reportError(reason) }
        : { dashboard: null, phase: "error", error: reportError(reason), refreshing: false, refreshError: null } };
    }
    publish(reports);
  };
  void load("current", scope);
  if (prior) void load("previous", prior);
  return () => { active = false; };
}
